import React from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { PreviewSection } from "../shared";
import { TextInput } from "@/components/ui/TextInput";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { InvestigationType } from "@/types/db/investigations";

// Inline input components
import { UserInput } from "@/components/inputs/UserInput";
import { IncidentTypeInput } from "@/components/inputs/IncidentTypeInput";
import { IncidentsInput, type IncidentRef } from "@/components/inputs/IncidentsInput";

export interface InvestigationData {
  id: string;
  name: string;
  description?: string | null;
  details?: string | null;
  status?: string;
  incidents?: IncidentRef[];
  meta_data?: {
    lead_investigator_id?: string;
    incident_type_id?: string;
    findings?: string;
    recommendations?: string;
  };
}

export interface InvestigationOverviewTabProps {
  investigation: InvestigationData | null;
  loading?: boolean;
}

export function InvestigationOverviewTab({ investigation, loading = false }: InvestigationOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<InvestigationType>();

  // Unified data source
  const displayData = editing ? formData : investigation;

  // ---------- Handlers ----------

  const handleLeadInvestigatorChange = (userId: string | null) => {
    const currentMeta = (formData?.meta_data as Record<string, any>) || {};
    updateFormData({
      ...formData,
      meta_data: {
        ...currentMeta,
        lead_investigator_id: userId || undefined,
      },
    } as any);
  };

  const handleIncidentTypeChange = (typeId: string | null) => {
    const currentMeta = (formData?.meta_data as Record<string, any>) || {};
    updateFormData({
      ...formData,
      meta_data: {
        ...currentMeta,
        incident_type_id: typeId || undefined,
      },
    } as any);
  };

  const handleIncidentsChange = (incidents: IncidentRef[]) => {
    updateFormData({
      ...formData,
      incidents: incidents as any,
    } as any);
  };

  // Read incidents from the jsonb column
  const incidentsValue: IncidentRef[] = (displayData as any)?.incidents || [];

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 16 }}>
        {/* Lead Investigator */}
        <UserInput
          value={(displayData?.meta_data as any)?.lead_investigator_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Lead Investigator"
          placeholder="Select lead investigator (optional)"
          onChange={handleLeadInvestigatorChange}
        />

        {/* Incident Type */}
        <IncidentTypeInput
          value={(displayData?.meta_data as any)?.incident_type_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Incident Type"
          placeholder="Select incident type (optional)"
          onChange={handleIncidentTypeChange}
        />

        {/* Linked Incidents */}
        <IncidentsInput
          value={incidentsValue}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Linked Incidents"
          onChange={handleIncidentsChange}
        />

        {/* Description */}
        <PreviewSection title="Description" titleStyle={styles.label}>
          <TextInput
            value={displayData?.description || ""}
            onChangeText={(text) => updateFormData({ ...formData, description: text })}
            multiline
            numberOfLines={3}
            editable={editing}
            loading={loading}
            placeholder="Describe the investigation..."
          />
        </PreviewSection>

        {/* Details */}
        <PreviewSection title="Details" titleStyle={styles.label}>
          <TextInput
            value={displayData?.details || ""}
            onChangeText={(text) => updateFormData({ ...formData, details: text })}
            multiline
            numberOfLines={5}
            editable={editing}
            loading={loading}
            placeholder="Additional details..."
          />
        </PreviewSection>

        {/* Findings */}
        <PreviewSection title="Findings" titleStyle={styles.label}>
          <TextInput
            value={(displayData?.meta_data as any)?.findings || ""}
            onChangeText={(text) => {
              const currentMeta = (formData?.meta_data as Record<string, any>) || {};
              updateFormData({
                ...(formData as any),
                meta_data: {
                  ...currentMeta,
                  findings: text || undefined,
                },
              });
            }}
            multiline
            numberOfLines={4}
            editable={editing}
            loading={loading}
            placeholder="Document investigation findings..."
          />
        </PreviewSection>

        {/* Recommendations */}
        <PreviewSection title="Recommendations" titleStyle={styles.label}>
          <TextInput
            value={(displayData?.meta_data as any)?.recommendations || ""}
            onChangeText={(text) => {
              const currentMeta = (formData?.meta_data as Record<string, any>) || {};
              updateFormData({
                ...(formData as any),
                meta_data: {
                  ...currentMeta,
                  recommendations: text || undefined,
                },
              });
            }}
            multiline
            numberOfLines={4}
            editable={editing}
            loading={loading}
            placeholder="Document recommendations..."
          />
        </PreviewSection>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});
